<script context="module">
  function validatorPostCode(value) {
    return /^[0-9a-zA-Z\s]{3,20}$/.test(value);
  }

  function validatorIDNumber(value) {
    return /^[0-9a-zA-Z\s]{5,50}$/.test(value);
  }

  function getByteLength(str) {
    let length = 0;
    for (let i = 0; i < str.length; i++) {
      const charCode = str.charCodeAt(i);
      if (charCode <= 0x007f) {
        length += 1; // ASCII字符
      } else {
        length += 2; // 非ASCII字符，占2个字节
      }
    }
    return length;
  }

  function validatorStrLen(str, maxLen, minLen = 0) {
    const strLen = getByteLength(String(str));
    if (strLen > maxLen || String(str).length < minLen) {
      return false;
    }
    return true;
  }
</script>

<script>
  import {createEventDispatcher, onMount} from 'svelte';
  import {Button} from '@px/button';
  import {SERVICES} from '@/const/services';
  import {createFun} from '@/helper/fun';
  import {mc} from '@/core';
  import {sd} from '@/da/sd';
  import {kycApi} from '@/api/kyc';
  import {userInfo$} from '@/store/user';
  import {logout$} from '@/streams/auth';
  import {countries$, regions$, basicKycInfo$, basicKycDialog$, bonus$, getBonus$, getCountries$, getRegions$, getBasicKycInfo} from './streams';
  import Modal from '@/widgets/Modal.svelte';
  import Input from '@/widgets/form/input/Input.svelte';
  import PhoneNumber from '@/widgets/form/input/PhoneNumber.svelte';
  import SelectSearch from '@/widgets/SelectSearch.svelte';
  import Select from '@/widgets/Select.svelte';
  import DatepickerFormItem from '@/widgets/DatepickerFormItem.svelte';
  import lang from './BasicKycDialog.lang';

  export let LG;
  export let closeable;
  export let callback;

  const dispatch = createEventDispatcher();
  const shieldIcon = `${SERVICES.STATIC_URL}/pubimg/66928f9779901d567eab1171acf8541c63769183b6f4e23c926819e8b26a83b7.png`;
  const giftIcon = `${SERVICES.STATIC_URL}/pubimg/cfbf8bca49e34ba0a2c9c8353b17abd9ee5d8545479a4f416b6b0b4d5636c543.png`;

  let documentTypes = [];
  let formData = {
    id: null,
    userId: $userInfo$.userId,
    country: '',
    province: '',
    city: '',
    birthday: '',
    address: '',
    zipCode: '',
    firstName: '',
    lastName: '',
    documentType: 1,
    documentNumber: '',
    phoneNumber: 0,
    countryCode: '',
  };
  let loading = false;
  let haveNoCountryCode = false;
  let countryCode = 0;

  let formDataValid = {
    province: {valid: null, maxLen: 100, minLen: 1},
    city: {valid: null, maxLen: 100, minLen: 1},
    firstName: {valid: null, maxLen: 50, minLen: 1},
    lastName: {valid: null, maxLen: 50, minLen: 1},
    phoneNumber: {valid: null, maxLen: 20, minLen: 3},
    address: {valid: null, maxLen: 100, minLen: 1},
    zipCode: {valid: null, validator: validatorPostCode},
    documentNumber: {valid: null, validator: validatorIDNumber},
  };

  $: updateCode(haveNoCountryCode, countryCode);
  $: submitDisabled =
    Object.entries(formData)
      .filter(([k]) => k !== 'id')
      .some(([k, v]) => !v) || loading;

  $: regionsList = updateRegionsList($regions$, formData.country);

  onMount(() => {
    sd.track('basic_information_exposure');
    getRegions$.next();
    getCountries$.next();
    getBasicKycInfo();
    if (!callback) {
      getBonus$.next();
    }
    const sub = basicKycInfo$.subscribe(() => {
      Object.keys(formData).forEach((key) => {
        if ($basicKycInfo$[key]) {
          formData[key] = $basicKycInfo$[key];
          if ($basicKycInfo$.phoneNo) {
            countryCode = $basicKycInfo$.phoneNo.split('-')[0];
            formData.countryCode = $basicKycInfo$.phoneNo.split('-')[0];
            formData.phoneNumber = $basicKycInfo$.phoneNo.split('-')[1];
          }
        }
      });
      if ($basicKycInfo$.country) {
        getDocumentTypes($basicKycInfo$.country);
        getCountryCode($basicKycInfo$.country);
      }
    });

    return function () {
      sub.unsubscribe();
    };
  });

  function updateCode(haveNoCountryCode, countryCode) {
    if (haveNoCountryCode && countryCode) {
      formData.countryCode = countryCode;
    }
  }

  function updateRegionsList(regions, country) {
    if (!regions || !regions[country]) {
      return [];
    }
    return regions[country].map((value) => ({text: value, value})).concat({text: 'Other', value: 'Other'});
  }

  function handleClickClose() {
    sd.track('basic_information_button_click', {button_name: 'skip'});
    closeDialog();
  }

  function closeDialog() {
    localStorage.removeItem('base-kyc-status');
    basicKycDialog$.next();
    dispatch('close');
  }

  async function getDocumentTypes(country) {
    const {data, error} = await kycApi.getDocumentTypesByCountry(country);
    if (data) {
      documentTypes = Object.entries(data).map(([key]) => {
        return {
          text: LG(lang[`Id${key}`]),
          value: Number(key),
        };
      });
    }
    if (error) {
      mc.send('toast', 'error', error);
    }
  }

  function getCountryCode(country) {
    haveNoCountryCode = false;
    const countryList = $countries$.find((o) => o.value === country);
    if (country === 'OTH') {
      haveNoCountryCode = true;
      countryCode = 1;
      return;
    }
    if (!countryList) {
      formData.country = 'OTH';
      countryCode = 1;
      haveNoCountryCode = true;
      return;
    }
    formData.countryCode = countryList.countryCode;
  }

  function handlerCountryChange(e) {
    formData.documentType = 1;
    formData.documentNumber = '';
    getDocumentTypes(e.detail.value);
    getCountryCode(e.detail.value);
  }

  async function handleSubmitButtonClick() {
    sd.track('basic_information_button_click', {button_name: 'submit'});

    if (!validatorFields(formData)) {
      sd.track('basic_information_validator_failed', {button_name: 'submit', formData});
      return;
    }

    loading = true;
    const params = {
      ...formData,
      phoneNo: (haveNoCountryCode ? countryCode : formData.countryCode) + '-' + formData.phoneNumber,
      countryName: $countries$.find((c) => c.value === formData.country).text,
    };
    const {data, error} = await kycApi.setBasicKycInfo(params);
    loading = false;
    if (data) {
      if (callback) {
        callback();
      }
      closeDialog();
    }
    if (error) {
      mc.send('toast', 'error', error);
    }
  }

  function handleClickLogout() {
    sd.track('basic_information_button_click', {button_name: 'log_out'});
    logout$.next({manual: true});
  }

  function handleDocumentTypeChange() {
    formData.documentNumber = '';
  }

  function validatorFields(fields) {
    return Object.keys(fields)
      .map((key) => {
        return validatorField(key, fields[key]);
      })
      .every((o) => !!o);
  }

  function validatorField(fieldName, value) {
    if (formDataValid.hasOwnProperty(fieldName)) {
      const field = formDataValid[fieldName];
      let valid = true;
      if (field.hasOwnProperty('maxLen') || field.hasOwnProperty('minLen')) {
        valid = validatorStrLen(value, field.maxLen, field.minLen || 0);
      }
      if (field.hasOwnProperty('validator')) {
        valid = field.validator(value);
      }
      formDataValid[fieldName].valid = valid;
      return valid;
    }
    return true;
  }
</script>

<style>
  .wrap {
    padding: 0 6px;
  }
  .tip {
    padding: 10px 12px;
    background: #f2f5fe;
    color: #003fe6;
  }
  .form-item {
    margin-bottom: 24px;
  }
  .label {
    margin-bottom: 8px;
    color: #303236;
  }
  .logout,
  .iconfont {
    right: 30px;
  }
  .col {
    display: flex;
  }
  .err {
    margin-top: 4px;
    font-size: 14px;
    color: var(--sell);
  }
  :global(.modal-basic-kyc .border, .modal-basic-kyc .pm-input) {
    border-radius: 4px !important;
    border-color: #dbdfe7 !important;
  }
  @media screen and (max-width: 640px) {
    :global(.modal-basic-kyc .modal) {
      border-bottom-right-radius: 0 !important;
      border-bottom-left-radius: 0 !important;
      width: 100% !important;
    }
    :global(.modal-basic-kyc .modal-mask .container) {
      align-items: flex-end;
    }
    .tip {
      padding: 8px;
    }
    .col {
      display: block;
    }
    form {
      height: calc(100vh - 3em - 198px);
      overflow-x: hidden;
      overflow-y: auto;
    }
    .form-item {
      margin-bottom: 20px;
    }
    .col .ml10 {
      margin-left: 0;
    }
    .logout,
    .iconfont {
      right: 16px;
    }
  }
</style>

<Modal visible class="modal-basic-kyc">
  <div class="wrap">
    {#if closeable}
      <i class="iconfont pa f30 T4 cp" on:click={handleClickClose}>&#xe614;</i>
    {:else}
      <span class="logout pa f14 T4 tdu cp" on:click={handleClickLogout}>{LG(lang.Logout)}</span>
    {/if}
    <div class="mb20 f18 b">{LG(lang.Title)}</div>
    {#if callback}
      <div class="tip df aic mb18 br4 f14">
        <img class="mr8" src={shieldIcon} width="18" height="18" alt="shield-icon" />
        <span>{LG(lang.Tip)}</span>
      </div>
    {:else}
      <div class="tip df aic mb18 br4 f14">
        <img class="mr8" src={giftIcon} width="18" height="18" alt="gift-icon" />
        <span>{@html LG(lang.Bonus, {Bonus: $bonus$})}</span>
      </div>
    {/if}
    <form>
      <div class="form-item">
        <div class="label">{LG(lang.CountryRegion)}</div>
        <SelectSearch type="border" options={$countries$} bind:value={formData.country} on:change={handlerCountryChange} />
      </div>
      <div class="form-item">
        <div class="label">{LG(lang.Province)}</div>
        {#if regionsList.length === 0}
          <Input bind:value={formData.province} on:blur={createFun(validatorField, 'province', formData.province)} />
          {#if formDataValid.province.valid === false}
            <div class="err">{LG(lang.FieldValidError)}</div>
          {/if}
        {:else}
          <SelectSearch type="border" options={regionsList} bind:value={formData.province} />
        {/if}
      </div>
      <div class="form-item mt24">
        <div class="label mb8 f14 T4">{LG(lang.City)}</div>
        <Input bind:value={formData.city} on:blur={createFun(validatorField, 'city', formData.city)} />
        {#if formDataValid.city.valid === false}
          <div class="err">{LG(lang.FieldValidError)}</div>
        {/if}
      </div>
      <div class="form-item">
        <div class="label">{LG(lang.LastName)}</div>
        <Input bind:value={formData.lastName} on:blur={createFun(validatorField, 'lastName', formData.lastName)} />
        {#if formDataValid.lastName.valid === false}
          <div class="err">{LG(lang.FieldValidError)}</div>
        {/if}
      </div>
      <div class="form-item">
        <div class="label">{LG(lang.FirstName)}</div>
        <Input bind:value={formData.firstName} on:blur={createFun(validatorField, 'firstName', formData.firstName)} />
        {#if formDataValid.firstName.valid === false}
          <div class="err">{LG(lang.FieldValidError)}</div>
        {/if}
      </div>
      <div class="form-item">
        <div class="label">{LG(lang.Birthday)}</div>
        <DatepickerFormItem bind:value={formData.birthday} {LG} />
      </div>
      <div class="form-item">
        <div class="label">{LG(lang.PhoneNumber)}</div>
        <PhoneNumber
          countryCode={formData.countryCode ? '+' + formData.countryCode : ''}
          bind:value={formData.phoneNumber}
          bind:code={countryCode}
          {haveNoCountryCode}
          on:blur={createFun(validatorField, 'phoneNumber', formData.phoneNumber)}
        />
        {#if formDataValid.phoneNumber.valid === false}
          <div class="err">{LG(lang.FieldValidError)}</div>
        {/if}
      </div>
      <div class="col">
        <div class="form-item f1">
          <div class="label">{LG(lang.Address)}</div>
          <Input bind:value={formData.address} on:blur={createFun(validatorField, 'address', formData.address)} />
          {#if formDataValid.address.valid === false}
            <div class="err">{LG(lang.FieldValidError)}</div>
          {/if}
        </div>
        <div class="form-item f1 ml10">
          <div class="label">{LG(lang.ZipCode)}</div>
          <Input bind:value={formData.zipCode} on:blur={createFun(validatorField, 'zipCode', formData.zipCode)} />
          {#if formDataValid.zipCode.valid === false}
            <div class="err">{LG(lang.FieldValidError)}</div>
          {/if}
        </div>
      </div>
      <div class="form-item">
        <div class="label">{LG(lang.Certificate)}</div>
        <Select type="border" bind:value={formData.documentType} options={documentTypes} on:change={handleDocumentTypeChange} />
      </div>
      <div class="form-item">
        <div class="label">{LG(lang.Id)}</div>
        <Input bind:value={formData.documentNumber} on:blur={createFun(validatorField, 'documentNumber', formData.documentNumber)} />
        {#if formDataValid.documentNumber.valid === false}
          <div class="err">{LG(lang.FieldValidError)}</div>
        {/if}
      </div>
    </form>
    <Button class="wp100" type="primary" {loading} disabled={submitDisabled} on:click={handleSubmitButtonClick}>
      {LG(lang.Submit)}
    </Button>
  </div>
</Modal>
